import useGoalProperty from '@/composables/property/goal-property';
import { CREATED_AT, MODIFIED_AT } from 'shared/api/query/constants';
import { computed, unref } from 'vue';
import { directProperties as directGoalActivityProperties } from '@/lib/goal-activity/properties';
import { directProperties as directUpdateProperties } from '@/lib/updates/properties';
import { goalActivity as goalActivityConfig, goal as goalConfig, update as updateConfig } from 'shared/api/query/configs.json';
import { isOrphanChartRef, knownChartRefKeys } from '@/composables/grid-page/chart/utils';
import { propertyType } from 'shared/constants.json';
import { useI18n } from 'vue-i18n';

// Canonical list of refs a chart can target for a given `source`. This is the
// single source of truth shared by the chart editor's selector dropdowns and
// chart-data-query's orphan-ref guard. Adding or removing an entry here is
// what defines whether a saved chart ref is "orphan" — so both the dynamic
// (property deleted by user) and direct (edge removed from
// lib/<entity>/properties.js) deletion paths surface as the same signal.
export default function useChartProps(source) {
  const { t } = useI18n();
  const { properties: goalProperties } = useGoalProperty();

  const directRef = (p) => ({ isProperty: false, edge: p.edgeName, edgeType: p.type, label: p.label });

  const availableRefs = computed(() => {
    switch (unref(source)) {
      case goalConfig.model:
        return [
          { isProperty: false, edge: CREATED_AT, edgeType: propertyType.date },
          { isProperty: false, edge: MODIFIED_AT, edgeType: propertyType.date },
          { isProperty: false, edge: goalConfig.edges.cachedCalculatedCurrent, edgeType: propertyType.number },
          { isProperty: false, edge: goalConfig.edges.goalCycle, edgeType: propertyType.options },
          ...goalProperties.value
            .filter((p) => p.type !== propertyType.richText)
            .map((p) => ({ isProperty: true, property: p })),
        ];
      case updateConfig.model:
        return directUpdateProperties(t).map(directRef);
      case goalActivityConfig.model:
        return directGoalActivityProperties(t).map(directRef);
      default:
        return [];
    }
  });

  const knownKeys = computed(() => knownChartRefKeys(availableRefs.value));

  const isOrphanRef = (ref) => isOrphanChartRef(ref, knownKeys.value);

  return { availableRefs, isOrphanRef };
}
